import { get, includes } from 'lodash';

import axios from 'axios';
import axiosRetry from 'axios-retry';
import settings from '../../config/settings';
import UtilsService from '../services/UtilsService';
import TasksHttpService from './TasksHttpService';
import AuthenticationHttpService from './AuthenticationHttpService';
import ImpersonationService from '../services/ImpersonationService';
import { EmployerRepAuthTypes } from '../constants/employerRepaAuthTypes';
import Logger from '../services/logger';

export const defaultConfig = {
    baseURL: `${settings.BackendUrl}/`,
    withCredentials: true,
};

export class ConnectorWrapper {
    constructor() {
        this.setupClient(defaultConfig);
    }

    setupClient(config) {
        this.axios = axios.create(config);
        // Configure axios-retry
        axiosRetry(this.axios, {
            retries: 3, // Number of retry attempts
            retryDelay: (retryCount) => axiosRetry.exponentialDelay(retryCount), // Exponential backoff delay
            retryCondition: (error) => {
                Logger.error(error);
                if (error?.response) {
                    return error.response?.status === 502 || error.response?.status === 504;
                }
                const retryErrorCodes = ['ERR_NETWORK', 'ECONNRESET'];
                if (error?.code) {
                    return retryErrorCodes.includes(error.code);
                }
                Logger.error(`Error: ${error?.code || error?.message}`);
                return false;
            },
        });
    }

    setHeaders(headers) {
        this.setupClient({
            ...defaultConfig,
            headers,
        });
    }

    addHeaders(headers) {
        this.setupClient({
            ...defaultConfig,
            headers: {
                ...this.axios.defaults.headers,
                ...headers,
            },
        });
    }

    async get(...args) {
        let result = null;
        try {
            const response = await this.axios.get(...args);

            result = get(response, 'data.result');
        } catch ({ response: errorResponse }) {
            if (this.isAuthError(errorResponse)) {
                if (args[0] === 'logout') {
                    await this.logout();
                }
                await this.authErrorHandler(errorResponse);
            } else if (this.isNotFoundError(errorResponse)) {
                return null;
            } else {
                const message = get(
                    errorResponse,
                    'data.message',
                    'Unknown GET error',
                );
                throw new Error(message);
            }
        }
        return result;
    }

    isAuthError(response) {
        return includes([401, 403], response?.status);
    }

    isNotFoundError(response) {
        return includes([404], response?.status);
    }

    isDataAccessError(response) {
        return includes([403], response?.status);
    }

    async authErrorHandler(response) {
        if (this.isDataAccessError(response)) {
            await AuthenticationHttpService.logout().finally(() => {
                window.location.reload();
            });

            return;
        }

        if (this.isAuthError(response)) {
            let authData = UtilsService.parseJSON(decodeURIComponent(AuthenticationHttpService.getCookie('authData')));
            if (!authData?.employerId) {
                const positionId = ImpersonationService.getPositionIdFromRoute({ pathname: window.location.pathname });
                const employerId = ImpersonationService.getEmployerIdFromRoute({ pathname: window.location.pathname });
                authData = await AuthenticationHttpService.getAuthData({
                    positionId,
                    employerId,
                }).then((res) => {
                    const authDataWithEmployerId = {
                        ...res?.authData,
                        employerId: res?.employerId,
                    };
                    return authDataWithEmployerId;
                });
            }
            if (authData?.employerId && authData?.authType === EmployerRepAuthTypes.SSO) {
                await AuthenticationHttpService.logout().finally(() => {
                    window.location.reload();
                });
            } else {
                AuthenticationHttpService.logout().finally(async () => {
                    await this.logout();
                });
            }
        }
    }

    async logout() {
        let isOnlyWebAppLoginAllowed = true;
        try {
            const systemPreferences = await this.get('/system/preferences');
            isOnlyWebAppLoginAllowed = get(systemPreferences, 'isOnlyWebAppLoginAllowed', true);
        } finally {
            if (isOnlyWebAppLoginAllowed) {
                window.location.href = `${window.location.origin}/login`;
            } else {
                window.location.href = `${settings.TFTAppUrl}/#/login/dropAuth`;
            }
        }
    }

    async post(...args) {
        try {
            const response = await this.axios.post(...args);
            const message = response?.data?.message;
            const returnErrMsg = get(args, '[2].returnErrMsg', false);
            return get(response, 'data.result', returnErrMsg && message ? { message } : undefined);
        } catch (error) {
            const response = error?.response;
            if (args[0] === '/login') {
                if (includes(response?.data?.message, 'not active')) {
                    window.location.href = `${window.location.origin}/inactive-user-page`;
                }
                throw response?.data;
            }
            const handleAuthError = get(args, '[2].handleAuthError', true);

            const hasCancelReason = !!get(args, '[2].cancelToken.reason');
            const isCancelError = axios.isCancel(error) || hasCancelReason;

            if (handleAuthError && !isCancelError) {
                await this.authErrorHandler(response);
            }

            if (isCancelError) {
                return Promise.resolve({ isCancelled: true });
            }

            if (response?.data?.errorCode === 'VALIDATION_FAIL') {
                throw response.data;
            }

            const message = response?.data?.message || 'Unknown error';

            throw new Error(message);
        }
    }

    async put(...args) {
        let result = null;
        try {
            const response = await this.axios.put(...args);

            result = get(response, 'data.result');
        } catch ({ response }) {
            if (this.isAuthError(response)) {
                await this.authErrorHandler(response);
            } else {
                if (response.data.errorCode === 'VALIDATION_FAIL') {
                    throw response.data;
                }
                const { message } = response.data;

                throw new Error(message);
            }
        }
        return result;
    }

    async delete(...args) {
        let result = null;
        try {
            const response = await this.axios.delete(...args);

            result = get(response, 'data.result');
        } catch ({ response }) {
            if (this.isAuthError(response)) {
                await this.authErrorHandler(response);
            } else {
                const { message } = response.data;

                throw new Error(message);
            }
        }

        return result;
    }

    async backoff({
        request,
        attempts = 5,
        delay: delayMs = 100,
        multiply = 2,
    }) {
        try {
            const result = await request();
            return result;
        } catch (error) {
            if (attempts > 0) {
                await UtilsService.delay(delayMs);
                return this.backoff({
                    request,
                    attempts: attempts - 1,
                    delay: delayMs * multiply,
                    multiply,
                });
            }
            throw error;
        }
    }

    async pollUntilTaskFinished(taskId, pollIntervalMs = 500) {
        const taskData = await TasksHttpService.getTaskData(taskId);
        if (taskData?.status === 'processing') {
            await UtilsService.delay(pollIntervalMs);
            return this.pollUntilTaskFinished(taskId);
        }

        if (taskData?.status === 'complete') {
            return taskData?.results;
        }

        if (taskData?.status === 'error') {
            throw new Error('Failed to complete task');
        }

        return null;
    }
}

export default new ConnectorWrapper();
